import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Formats a date range string from "Monday, November 17 - Saturday, November 22"
 * to "Mon, Nov 17 ~ Sat, Nov 22"
 * @param dateRangeString - The date range string to format
 * @returns Formatted date range string
 */
export const formatEventDateRange = (dateRangeString: string): string => {
  if (!dateRangeString) {
    return '';
  }

  // Split by " - " to get start and end dates
  const parts = dateRangeString.split(' - ');

  if (parts.length !== 2) {
    // If it doesn't match expected format, return as is
    return dateRangeString;
  }

  const [startDateStr, endDateStr] = parts;

  try {
    // Parse the dates - format: "Monday, November 17"
    // Extract month name and day number
    const parseDate = (dateStr: string): dayjs.Dayjs | null => {
      // Remove day name (e.g., "Monday, ")
      const datePart = dateStr.replace(/^[^,]+, /, '').trim();

      // Parse format: "November 17" or "November 17, 2024"
      // Try parsing with current year first
      const currentYear = dayjs().year();
      const formats = [
        `MMMM D, YYYY`, // "November 17, 2024"
        `MMMM D`, // "November 17"
      ];

      for (const format of formats) {
        const parsed = dayjs(datePart, format);
        if (parsed.isValid()) {
          // If no year was parsed, use current year
          if (!datePart.includes(',')) {
            return parsed.year(currentYear);
          }
          return parsed;
        }
      }

      return null;
    };

    const startDate = parseDate(startDateStr.trim());
    const endDate = parseDate(endDateStr.trim());

    if (!startDate || !endDate) {
      return dateRangeString;
    }

    // Format as "Mon, Nov 17"
    const formattedStart = startDate.format('ddd, MMM D');
    const formattedEnd = endDate.format('ddd, MMM D');

    return `${formattedStart} ~ ${formattedEnd}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return dateRangeString;
  }
};
